import React, {Component} from 'react';
import { CircleQuestionMark } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Tooltip } from "components/tooltip/Tooltip";
import translate from '../translations/translations.wrapper.jsx';
import ConfigurationWrapper from '../configuration/ConfigurationWrapper.component';
import * as Api from '../../utils/api/api';
import {handleError, insufficientRightsError} from "utils/errorHandle.function";
import Confirm from '../popup/ConfirmActionPopup.component';
import * as reduxSelectors from '../store/application.reducers';
import {connect} from 'react-redux';
import {EditDeleteButtons, SaveCancelButtons} from 'utils/listActionButtons.component';

import {userHasAccessRights} from "utils/accessRights.function";
import {canViewConfiguration} from "../menu/Menu.component";
import { Button, ButtonType } from "components/common/Button.component";
import { TextInput } from "components/common/TextInput.component";
import "components/listWithActions/list_gaia.scss";
import { MODAL_ACTIONS } from "utils/constants";
import { launchDarkly } from "utils/launchDarkly";
import { useModalContext } from "components/modals/ModalContext";

export class Roles extends ConfigurationWrapper {

    constructor(props) {
        super(props);
        this.state = {
            roles: [],
            showNewRow: false,
            confirmDelete: false,
            warningMessage: "",
            toDelete: null
        };

        this.addRow = this.addRow.bind(this);
        this.cancelAdd = this.cancelAdd.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.deleteRow = this.deleteRow.bind(this);
        this.getData = this.getData.bind(this);
        this.updateRow = this.updateRow.bind(this);
    }

    componentDidMount(props) {
        if (!userHasAccessRights(this.props.userData.userRoles, this.props.location) || !canViewConfiguration(this.props.userData.userRoles)) {
            handleError(insufficientRightsError);
        }

        super.componentDidMount(props);
    }

    addRow(name) {
        Api.addRuleConfigurationRoles(this.props.currentContextId, name)
            .then(() => {
                this.getData();
            })
            .catch((error) => {
                console.log(error);
            });
    }

    cancelAdd() {
        this.setState({
            showNewRow: false
        });
    }

    confirmDelete() {
        Api.deleteRuleConfigurationRoles(this.props.currentContextId, this.state.toDelete, true).then(result => {
            this.getData();
        }, error => {
                handleError(error);
        });
        this.setState({confirmDelete: false})
    }

    deleteRow(id) {
        if (this.props.useNewModals && this.props.toggleModal) {
            this.setState({toDelete: id}, () => {
                this.props.toggleModal(MODAL_ACTIONS.CONFIRM_ACTION, {
                    message: this.props.translate("roles.confirmDelete"),
                    handleAction: () => {
                        this.props.toggleModal(null);
                        this.confirmDelete();
                    },
                });
            });
        } else {
            this.setState({
                confirmDelete: true,
                toDelete: id
            });
        }
    }

    getData() {
        Api.getRuleConfigurationRoles(this.props.currentContextId).then(result => {
            this.setState({
                roles: result,
                showNewRow: false
            });
        });

    }

    updateRow(data) {
        Api.updateRuleConfigurationRoles(this.props.currentContextId, data).then(result => {
            this.getData();
        });
    }


    buildContent() {
        let readOnly = this.props.userRoles.systemViewer && !this.props.userRoles.systemAdministrator;

        let roles = this.state.roles.map((role, index) => {
            return (<RoleRow key={role.roleId} role={role}
                             readOnly={readOnly}
                             updateRow={this.updateRow}
                             deleteRow={this.deleteRow}
                             translate={this.props.translate}/>)
        });

        return <main className="page w-100 mt-48 overflow-y-auto d-flex justify-content-center align-items-start">
            <div className="py-16 mb-48 w-50 settings-page__container">
                {this.state.confirmDelete && !this.props.useNewModals &&
                <Confirm translate={this.props.translate}
                         message={this.props.translate("roles.confirmDelete")}
                         handleAction={this.confirmDelete}
                         closeCallback={() => {
                             this.setState({confirmDelete: false})
                         }}/>}

                <h2 className="ps-4 mb-4 d-flex align-items-center">{this.props.translate("roles.header")}
                    <span className="ms-4"
                          data-for="roles-header-info-tooltip" data-tip="">
                        <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                    </span>
                    <Tooltip
                        id="roles-header-info-tooltip"
                        place="bottom"
                        content={() => this.props.translate("roles.headerInfo")}
                    />
                </h2>

                <div className="bg-white radius-4 p-4 setting_collapsable-main">
                    <div className="actions-list">
                        <div className="row header-row mx-0">
                            <div className="col-md-9">
                                {this.props.translate("roles.approverRole")}
                            </div>
                            <div className="col-md-3 text-right col-action-buttons">
                                {this.props.translate("roles.actions")}
                            </div>
                        </div>
                        <div className={`main-panel radius-4 ${this.state.roles.length === 0 && !this.state.showNewRow ? 'd-flex align-items-center justify-content-center' : ''}`}>
                            {this.state.roles.length > 0 ?
                                roles
                                :
                                !this.state.showNewRow && (
                                    <h3 className="w-100 text-center m-0">
                                        {this.props.translate("roles.noMatches")}
                                    </h3>
                                )
                            }
                            {this.state.showNewRow &&
                            <NewRow addRow={this.addRow} cancelAdd={this.cancelAdd} translate={this.props.translate}/>
                            }
                        </div>

                        {!readOnly &&
                        <div className="d-flex justify-content-end mt-4">
                            <Button type={ButtonType.SECONDARY}
                                    onClick={() => {
                                        this.setState({showNewRow: true})
                                    }}
                                    text={this.props.translate("roles.add")}
                            />
                        </div>
                        }
                    </div>
                </div>
            </div>
        </main>
    }
}

const withTranslations = translate(Roles);
const mapStateToProps = function (store) {
    return {
        currentContextId: reduxSelectors.getUsersCurrentContextId(store),
        userRoles: reduxSelectors.getUsersRoles(store),
        userData: reduxSelectors.getUsersData(store)
    };
};

const Connected = connect(mapStateToProps, {})(withTranslations);

const RolesWithModal = (props) => {
    const useNewModals = launchDarkly.companyClient?.variation("use-new-modals", false);
    const modalContext = useModalContext();
    const {toggleModal} = modalContext || {};
    return <Connected {...props} useNewModals={useNewModals} toggleModal={toggleModal} />;
};

export default RolesWithModal;

class RoleRow extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editMode: false,
            roleName: props.role.name
        };

        this.changeRoleName = this.changeRoleName.bind(this);
        this.delete = this.delete.bind(this);
        this.enterEditMode = this.enterEditMode.bind(this);
        this.exitEditMode = this.exitEditMode.bind(this);
        this.save = this.save.bind(this);

    }

    changeRoleName(event) {
        this.setState({
            roleName: event.target.value
        });
    }

    delete() {
        this.props.deleteRow(this.props.role.roleId);
    }

    enterEditMode() {
        this.setState({
            editMode: true
        });
    }

    exitEditMode() {
        this.setState({
            editMode: false
        });
    }

    save() {
        let role = {
            name: this.state.roleName,
            idRole: this.props.role.roleId
        };
        this.props.updateRow(role);

        this.setState({
            editMode: false
        });
    }

    render() {
        if (this.state.editMode) {
            return (
                <div className="row">
                    <div className="col-md-9 align-self-center">
                        <TextInput className="ga-input__title"
                                   value={this.state.roleName}
                                   onChange={this.changeRoleName}/>
                    </div>

                    <div className="col-md-3 d-flex justify-content-end">
                        <SaveCancelButtons
                            saveCallback={this.save}
                            cancelCallback={this.exitEditMode}
                            translate={this.props.translate}
                        />
                    </div>
                </div>
            );
        }
        else {
            return (
                <div className="row">
                    <div className="col-md-9 align-self-center">{this.props.role.name}</div>

                    <div className="col-md-3 d-flex justify-content-end">
                        <EditDeleteButtons
                            disabled={this.props.editMode || this.props.readOnly}
                            editCallback={this.enterEditMode}
                            deleteCallback={this.delete}
                            translate={this.props.translate}
                        />
                    </div>

                </div>
            )
        }
    }
}

const NewRow = (props) => {
    let newName = null;
    let save = function () {
        if (newName)
            props.addRow(newName);
    };
    let nameValueChanged = function (event) {
        newName = event.target.value;
    };
    let cancel = function () {
        props.cancelAdd();
    };
    return (
        <div className="row">
            <div className="col-md-9 py-3">
                <TextInput onChange={nameValueChanged} className={"ga-input__title"}/>
            </div>

            <div className="col-md-3 d-flex justify-content-end">
                <SaveCancelButtons
                    saveCallback={save}
                    cancelCallback={cancel}
                    translate={props.translate}
                />
            </div>
        </div>
    )
};